import React, { useState, useEffect } from "react";
import { Button, Badge } from "./ui";
import { useLocation, useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBTable, MDBTableBody, MDBTableHead, MDBInput, MDBIcon } from "mdb-react-ui-kit";
import { api } from "../services";
import Toolbar from "./Toolbar";
import Actionbar from "./Actionbar";
import { useTranslation } from "react-i18next";
import ConfirmModal from "./Modals/ConfirmModal";
import { toast } from "react-toastify";

import { Copy, Check } from "lucide-react";

function ManageBranches() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const location = useLocation();
  const navigate = useNavigate();
  const { storeId, storeName } = location.state || {};
  const [branches, setBranches] = useState([]);
  const [admin, setAdmin] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [apps, setApps] = useState([]);
  const [showAvailable, setShowAvailable] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAutoAcceptConfirm, setShowAutoAcceptConfirm] = useState(false);

  const [copiedId, setCopiedId] = useState(null);

  const [copied, setCopied] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });
  const [autoAcceptModalData, setAutoAcceptModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });
  useEffect(() => {
    if (storeId) fetchStoreDetails();
  }, [storeId]);

  useEffect(() => {
    fetchApps();
  }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };
  const fetchStoreDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data.branches || []);
      setAdmin(response.data.admin || {});
    } catch (error) {
      console.error(
        "Error fetching store details:",
        error.response?.data || error.message
      );
    }
  };

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/store-apps/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Apps data:", response.data.apps);
      setApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching apps:", err.message);
    }
  };

  const handleAddBranch = () => {
    navigate("/add-branch", { state: { storeId, storeName } });
  };

  const handleViewBranch = (branchId) => {
    navigate("/view-branch", { state: { storeId, branchId } });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredBranches = branches.filter((branch) => {
    const name = isArabic
      ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
      : branch?.name?.en || branch?.name;
    return (name || "").toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getLatestSuccessfulSync = (branch) => {
    const syncedApps = branch.syncedApps || [];

    // Get only successful syncs with external IDs that match available apps
    const successfulSyncs = syncedApps.filter((sync) => {
      return apps.some((app) => app.application === sync.app);
    });

    // Group by app name and get the latest sync for each
    const syncsByApp = successfulSyncs.reduce((acc, sync) => {
      if (
        !acc[sync.app] ||
        new Date(sync.synced_date) > new Date(acc[sync.app].synced_date)
      ) {
        acc[sync.app] = {
          ...sync,
          isIntegrated: true,
          appDetails: apps.find((a) => a.application === sync.app),
        };
      }
      return acc;
    }, {});

    return Object.values(syncsByApp).sort((a, b) => {
      // Integrated apps first
      if (a.isIntegrated && !b.isIntegrated) return -1;
      if (!a.isIntegrated && b.isIntegrated) return 1;
      // Then by name
      return a.app.localeCompare(b.app);
    });
  };

  const handleAutoOrderAccept = async (
    branchId,
    appIntegrationId,
    externalId,
    autoAccept
  ) => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        appIntegrationId,
        externalId,
        autoOrderAccept: autoAccept,
      };

      const response = await api.post(
        `/branch/${branchId}/auto-order-status`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success(
          `${t(response.data.message || "Auto accept order status updated")}`
        );
        await fetchStoreDetails();
      } else {
        throw new Error(`Server responded with status ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error updating auto accept order status:", error);
      toast.error(
        `Failed to update auto accept order status. ${
          error.response?.data?.message || "Please try again later."
        }`
      );
    }
  };

  const handleStatusChange = async (
    branchId,
    appName,
    externalId,
    appId,
    status
  ) => {
    try {
      const payload = {
        appName,
        externalId,
        appId,
        status,
      };
      const token = localStorage.getItem("token");

      // console.log("🔄 Sending Data to update branch app status:", payload);

      const response = await api.post(`/branch/${branchId}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Check if the request was successful
      if (response.status >= 200 && response.status < 300) {
        console.log("✅ Status updated successfully:", response.data);
        // Optionally show a success notification to the user

        toast.success(`${t(response.data.message)}`);

        // Refresh the store details
        await fetchStoreDetails();
      } else {
        throw new Error(`Server responded with status ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error updating status:", error);

      // Show error message to user
      // alert(
      //   `Failed to update status for ${appName}. ${
      //     error.response.data.message || "Please try again later."
      //   }`
      // );
      toast.error(
        `Failed to update status for ${appName}. ${
          error.response.data.message || "Please try again later."
        }`
      );

      // You might want to add more specific error handling here
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error status:", error.response.status);
        console.error("Error headers:", error.response.headers);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error setting up request:", error.message);
      }
    }
  };

  const handleDeleteBranch = async (branchId) => {
    // if (
    //   !window.confirm(
    //     `${t("Are you sure you want to delete this branch? This action cannot be undone.")}`
    //   )
    // )
    //   return;
    try {
      const token = localStorage.getItem("token");
      const payload = {
        branchId,
      };
      const response = await api.post(`/branch`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status >= 200 && response.status < 300) {
        toast.success(
          response.data.message || `${t("Branch deleted successfully.")}`
        );
        // Refresh the branch list
        fetchStoreDetails();
      } else {
        toast.error(`${t("Failed to delete branch.")}`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Error deleting branch. Please try again."
      );
      console.error(error);
    }
  };

  return (
    <MDBContainer fluid className="p-4 content-area flex-grow-1">
      <h2>
        {storeName} - {t("branches")}
      </h2>
      <p>
        {t("Store ID")}: {storeId}{" "}
        {copied ? (
          <Check style={{ color: "green" }} size={15} />
        ) : (
          <Copy
            onClick={() => handleCopy(storeId)}
            style={{ cursor: "pointer" }}
            size={15}
          />
        )}
      </p>
      {/* Search and Add Branch */}
      <Actionbar
        title={t("quickAction")}
        subtitle={t("manageYouWorkSpace")}
        firstBtnText={
          showAvailable ? `${t("hideavailable")}` : `${t("showavailable")}`
        }
        firstBtnOnClick={() => setShowAvailable(!showAvailable)}
        firstBtnClass={showAvailable ? "ocean" : "enable-notifications"}
        firstBtnIcon="chart"
        secondBtnText={t("addnewbranch")}
        secondBtnOnClick={handleAddBranch}
        secondBtnClass="ocean"
        secondBtnIcon="plus"
      />
      <div className="mb-4">
        {" "}
        <Toolbar
          showSearch={true}
          searchPlaceHolder={t("Search branches")}
          searchQuery={searchQuery}
          handleSearch={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Branches Table */}
      <MDBTable responsive>
        <MDBTableHead className="table-header">
          <tr>
            <th className="custom-th">#</th>
            <th className="custom-th">{t("name")}</th>
            <th className="custom-th">{t("availability")}</th>
            <th className="custom-th">{t("integrationstatus")}</th>
            <th className="custom-th">{t("users")}</th>
            <th className="custom-th">{t("address")}</th>
            <th className="custom-th">{t("actions")}</th>
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {filteredBranches.map((branch, index) => (
            <tr key={branch._id}>
              <td>{index + 1}</td>
              <td>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <strong>
                    {isArabic
                      ? branch?.name?.ar ||
                        branch?.name_ar ||
                        branch?.name?.en ||
                        branch?.name
                      : branch?.name?.en || branch?.name}
                  </strong>
                  {branch.is_foodics_branch && (
                    <img
                      src="https://apidocs.foodics.com/assets/img/logo.png"
                      alt="Foodics"
                      title="Foodics Branch"
                      style={{
                        height: "10px",
                        width: "auto",
                        objectFit: "contain",
                        verticalAlign: "middle",
                      }}
                    />
                  )}
                  {branch.source_app &&
                    branch.source_app !== "manual" &&
                    !branch.is_foodics_branch && (
                      <Badge
                        color="info"
                        size="sm"
                        pill
                        style={{
                          fontSize: "0.65rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {branch.source_app}
                      </Badge>
                    )}
                </div>
              </td>
              <td>
                {(() => {
                  const daysOfWeek = [
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday",
                    "sunday",
                  ];

                  return daysOfWeek.map((day) => {
                    const dayData = branch.availableHours?.[day];

                    if (!dayData) return null;

                    const times = dayData.times || [];
                    const isVisible = dayData.is_visible;

                    return (
                      <div key={day}>
                        <strong style={{ textTransform: "capitalize" }}>
                          {t(day)}:
                        </strong>{" "}
                        {isVisible && times.length > 0 ? (
                          times
                            .map((slot) => `${slot.start} - ${slot.end}`)
                            .join(", ")
                        ) : (
                          <span className="text-muted">{t("unavailable")}</span>
                        )}
                      </div>
                    );
                  });
                })()}
              </td>

              <td>
                <div className="mb-2">
                  <div>
                    <strong>{t("Integration ID")}:</strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>{branch._id}</span>
                    <button
                      onClick={() => copyToClipboard(branch._id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        color: copiedId === branch._id ? "#28a745" : "#6c757d",
                      }}
                      title="Copy Integration ID"
                    >
                      {copiedId === branch._id ? (
                        <Check size={16} />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  {getLatestSuccessfulSync(branch).map((sync) => (
                    <div
                      key={sync._id || sync.external_id}
                      className="mb-2 p-2 border rounded bg-light"
                    >
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <Badge
                            color={
                              sync.synced_status && sync.external_id !== null
                                ? "success"
                                : sync.synced_status &&
                                  sync.external_id === null
                                ? "warning"
                                : "danger"
                            }
                            pill
                            className="px-3 py-2"
                          >
                            <strong>{sync.app}</strong>
                            <br />
                            <small> ID: {sync.external_id}</small>
                            <br />
                            <small className="text-white">
                              {sync.synced_status && sync.external_id !== null
                                ? "Available"
                                : sync.synced_status &&
                                  sync.external_id === null
                                ? "Pending"
                                : `${t("unavailable")}`}
                            </small>
                          </Badge>
                        </div>
                        {/* Auto Accept Orders toggle for all apps */}
                        <div className="mt-2">
                          <div className="d-flex align-items-center">
                            <span className="me-2">
                              {t("Auto Accept Orders:")}
                            </span>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`auto-accept-${branch._id}-${sync.app}`}
                                checked={sync.auto_order_accept || false}
                                onChange={(e) => {
                                  const isEnabling = e.target.checked;

                                  if (isEnabling) {
                                    // Show confirmation modal when enabling
                                    setAutoAcceptModalData({
                                      title: t("Confirm Auto-Accept Orders"),
                                      message: t(
                                        "You agree that you are allowing Loops to accept all orders continuously. If items are out of stock (OOS), you need to update the inventory."
                                      ),
                                      action: () =>
                                        handleAutoOrderAccept(
                                          branch._id,
                                          sync.integrationId || sync._id,
                                          sync.external_id || branch._id,
                                          true
                                        ),
                                      actionData: null,
                                      visibility: null,
                                    });
                                    setShowAutoAcceptConfirm(true);
                                  } else {
                                    // Directly disable without confirmation
                                    handleAutoOrderAccept(
                                      branch._id,
                                      sync.integrationId || sync._id,
                                      sync.external_id || branch._id,
                                      false
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Don't show available/unavailable buttons for Order Agent (Chat Agent) and Digital Catalog */}
                        {sync.app &&
                        !["Chat Agent", "Order Agent", "AI Order Agent", "Digital Catalog"].includes(sync.app) &&
                        // !sync.app.toLowerCase().includes("jahez") &&
                        ((sync.synced_status && sync.external_id !== null) ||
                          (!sync.synced_status &&
                            sync.external_id !== null)) ? (
                          <div className="d-flex align-items-center">
                            {sync.synced_status ? (
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                className="px-3"
                                onClick={() =>
                                  setModalData({
                                    title: "Confirm Action",
                                    message: t("confirmMarkUnavailable", {
                                      app: sync.app,
                                      id: sync.external_id,
                                    }),
                                    action: () =>
                                      handleStatusChange(
                                        branch._id,
                                        sync.app,
                                        sync.external_id,
                                        sync.integrationId,
                                        false
                                      ),
                                    actionData: sync.external_id,
                                    visibility: false,
                                  }) || setShowConfirm(true)
                                }
                                title={`Mark ${sync.app} as unavailable`}
                              >
                                <MDBIcon icon="times" className="me-1" />
                                {t("unavailable")}
                              </Button>
                            ) : (
                              <Button
                                color="success"
                                size="sm"
                                outline
                                className="px-3"
                                onClick={() =>
                                  setModalData({
                                    title: "Confirm Action",
                                    message: t("confirmMarkAvailable", {
                                      app: sync.app,
                                      id: sync.external_id,
                                    }),
                                    action: () =>
                                      handleStatusChange(
                                        branch._id,
                                        sync.app,
                                        sync.external_id,
                                        sync.integrationId,
                                        true
                                      ),
                                    actionData: sync.external_id,
                                    visibility: false,
                                  }) || setShowConfirm(true)
                                }
                                title={`Mark ${sync.app} as available`}
                              >
                                <MDBIcon icon="check" className="me-1" />
                                {t("available")}
                              </Button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                {showAvailable && (
                  <div className="mt-3">
                    <h6 className="text-muted mb-2">
                      {t("Available Apps for Integration")}:
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      {apps
                        .filter((app) => {
                          return !getLatestSuccessfulSync(branch).some(
                            (sync) => sync.app === app.application
                          );
                        })
                        .map((app) => (
                          <Badge
                            color="primary"
                            pill
                            key={app._id}
                            className="px-3 py-2"
                          >
                            <strong>{app.application}</strong>
                            <br />
                            <small>{t("Not Integrated")}</small>
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </td>
              <td>
                <strong>{admin.username}</strong>
                <br />
                <small>Super Admin</small>
                <br />
                <small>{branch.email}</small>
              </td>
              <td>{branch.address}</td>
              <td>
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={() => handleViewBranch(branch._id)}
                >
                  {t("view")}
                </Button>
                {showAvailable &&
                  getLatestSuccessfulSync(branch).length === 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={() =>
                          setModalData({
                            title: "Confirm Action",
                            message: `${t(
                              "Are you sure you want to delete this branch? This action cannot be undone."
                            )}`,
                            action: () => handleDeleteBranch(branch._id),
                            actionData: branch._id,
                            visibility: false,
                          }) || setShowConfirm(true)
                        }
                      >
                        {t("delete")}
                      </Button>
                    </div>
                  )}
              </td>
            </tr>
          ))}
        </MDBTableBody>
      </MDBTable>
      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={modalData.title}
        message={modalData.message}
        action={modalData.action}
        actionData={modalData.actionData}
        visibility={modalData.visibility}
      />
      <ConfirmModal
        show={showAutoAcceptConfirm}
        onHide={() => setShowAutoAcceptConfirm(false)}
        title={autoAcceptModalData.title}
        message={autoAcceptModalData.message}
        action={autoAcceptModalData.action}
        actionData={autoAcceptModalData.actionData}
        visibility={autoAcceptModalData.visibility}
      />
    </MDBContainer>
  );
}

export default ManageBranches;
